import { useEffect, useState } from "react";
import { CheckCircle2, Search } from "lucide-react";
import api, { money } from "@/lib/api";

export const ArtworkSetup = ({ options, fee, mode, onMode, reference, onReference, feeInCart }) => {
  const [lookup, setLookup] = useState(null);

  useEffect(() => {
    const ref = reference.trim();
    if (mode !== "reorder" || ref.length < 4) { setLookup(null); return; }
    let live = true;
    const t = setTimeout(() => {
      api.post("/artwork/lookup", { reference: ref }).then((r) => { if (live) setLookup(r.data); }).catch(() => { if (live) setLookup(null); });
    }, 350);
    return () => { live = false; clearTimeout(t); };
  }, [mode, reference]);

  return (
    <div data-testid="artwork-setup">
      <label className="font-mono-tech text-xs uppercase tracking-[0.2em] text-zinc-500 block mb-2">
        Artwork &amp; setup — <span className="text-zinc-300">one-time fee</span>
      </label>
      <p className="text-zinc-500 text-xs leading-relaxed mb-3" data-testid="artwork-setup-description">{fee.description}</p>
      <div className="space-y-2">
        {options.map((o) => {
          const on = mode === o.key;
          return (
            <button
              key={o.key}
              type="button"
              data-testid={`artwork-option-${o.key}`}
              aria-pressed={on}
              onClick={() => onMode(o.key)}
              className={`w-full text-left border transition-colors duration-300 px-4 py-3 flex items-start gap-4 ${on ? "border-[#C87455] bg-[#C87455]/10" : "border-zinc-800 hover:border-zinc-600"}`}
            >
              <span className={`mt-1 h-3.5 w-3.5 shrink-0 rounded-full border ${on ? "border-[#C87455] bg-[#C87455]" : "border-zinc-600"}`} />
              <span className="flex-1 min-w-0">
                <span className={`text-sm block ${on ? "text-white" : "text-zinc-300"}`}>{o.label}</span>
                <span className="text-zinc-500 text-xs block mt-1 leading-relaxed">{o.hint}</span>
              </span>
              <span className={`font-mono-tech text-xs shrink-0 ${on ? "text-[#C87455]" : "text-zinc-500"}`} data-testid={`artwork-option-price-${o.key}`}>
                {o.amount ? (feeInCart && on ? "In your order" : `+${money(o.amount)}`) : "No fee"}
              </span>
            </button>
          );
        })}
      </div>
      {mode === "reorder" && (
        <div className="mt-4 border border-zinc-800 bg-[#0A0A0A] p-4" data-testid="reorder-reference-block">
          <label className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-[#C87455] block mb-2">
            Previous order number or artwork reference <span className="text-zinc-500">· required</span>
          </label>
          <div className="relative">
            <Search size={13} className="absolute left-0 top-1/2 -translate-y-1/2 text-zinc-600" />
            <input
              data-testid="reorder-reference-input"
              value={reference}
              onChange={(e) => onReference(e.target.value.slice(0, 40))}
              placeholder="e.g. #3F2A9C1B or ART-7K2Q"
              className="w-full bg-transparent border-b border-zinc-800 focus:border-[#C87455] transition-colors rounded-none pl-6 pr-0 py-2 text-white placeholder-zinc-600 font-mono-tech text-sm tracking-widest uppercase"
            />
          </div>
          <p className="text-zinc-500 text-xs mt-2 leading-relaxed">Enter your previous order number or artwork reference so we can locate your design.</p>
          {lookup && (
            <p className={`font-mono-tech text-[10px] mt-3 flex items-center gap-2 ${lookup.found ? "text-emerald-400" : "text-amber-300"}`} data-testid={`reorder-lookup-${lookup.found ? "found" : "missing"}`}>
              {lookup.found ? <><CheckCircle2 size={12} /> Found on file — {lookup.label}</> : "Not found yet — you can still order; we'll verify your artwork before production."}
            </p>
          )}
        </div>
      )}
    </div>
  );
};
